import { calendarToday } from "../lib/days";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { summarise, type ExistingAssignment, type WriteOutcome } from "../lib/assign";
import { tellTheClient } from "../lib/assignPush";
import {
  afterTheBlockSentence, autoWeeksSentence, isMonday, weekOfDate, nextMondayISO, planProgram, tallyAddsUp, tallyOf,
  writeProgram, type ProgramDayRow,
} from "../lib/assignProgram";

/**
 * GIVE A BLOCK TO A CLIENT FROM A MONDAY (`docs/PROGRAMS-DESIGN.md` §3, §4).
 *
 * 🔴 IT CALLS `assign_coach_workout` ONCE PER DAY — the same RPC the phone and the AssignPanel
 * call — and it writes no assignment itself. The RPC is `SECURITY INVOKER`, clones the template
 * into a client-scoped copy (V7-2) and inserts the assignment in one statement; reproducing
 * those two writes here would be a second place a clone can drift from its source. §3.2 is the
 * argument for why there is no `assign_coach_program(...)` on the server.
 *
 * ⚠️ THE PLANNING AND THE WRITING ARE `lib/assignProgram.ts`, WHICH HAS NO CLIENT. This component
 * supplies the one injected `AssignCall` and draws the answer; the sequencing, the dates and the
 * tally are pure and mutation-tested there. A `Promise.all` written here would be the defect
 * that module's overlap-count test exists to catch, one layer up where nothing is watching.
 *
 * ⚠️ AND A WINDOW RATHER THAN A PAGE, unlike the board it opens from: it holds a decision taken
 * AGAINST the plan behind it — which weeks, whose week they land on — so the backdrop is what a
 * coach checks their answer against. That is `backdropRoute`'s own test for what earns one.
 */
export function AssignProgramWindow({ programId, programName, days, weeks, deloadWeeks = [], autoWeekCount = 0, open, onClose, onAssigned }: {
  programId: string;
  programName: string;
  days: readonly ProgramDayRow[];
  weeks: number;
  /** F15 3.9 — the board's deload marks: a WRITTEN week marked so is assigned with `is_deload` on each of its days, so the
   *  client's review says "deload week" and the week after progresses from the last LOADING week. */
  deloadWeeks?: readonly number[];
  /** F15 3.9 — how many AUTO weeks the block holds (nothing is assigned for them now). */
  autoWeekCount?: number;
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const firstRef = useRef<HTMLSelectElement>(null);
  const [clients, setClients] = useState<{ id: string; name: string }[] | null>(null);
  const [clientId, setClientId] = useState("");
  const [startISO, setStartISO] = useState(() => nextMondayISO(calendarToday()));
  const [existing, setExisting] = useState<ExistingAssignment[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => { if (open) firstRef.current?.focus(); }, [open]);

  /* ⚠️ THE ROSTER IS THE **ACCEPTED, ACTIVE** LINKS AND NOTHING ELSE. `coach_clients` also holds
     invitations with no account behind them (`client_id` null) — offering one would let a coach
     assign four weeks to somebody who has not joined, and the RPC would refuse each day with a
     message about a row rather than about a person. The predicate is the one every RLS policy
     since `20260906140000` uses: `status='active' AND accepted_at is not null`. */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const links = await supabase.from("coach_clients")
        .select("client_id")
        .eq("status", "active")
        .not("accepted_at", "is", null);
      if (cancelled) return;
      const ids = (links.data ?? []).map((l) => l.client_id).filter((i): i is string => i !== null);
      if (ids.length === 0) { setClients([]); return; }
      const profiles = await supabase.from("profiles")
        .select("id, first_name, last_name").in("id", ids);
      if (cancelled) return;
      const named = ids.map((id) => {
        const p = (profiles.data ?? []).find((x) => x.id === id);
        const full = [p?.first_name, p?.last_name].filter((s) => s !== null && s !== "").join(" ");
        /* A client whose profile could not be read is still a client — dropping them would hide
           somebody a coach can legitimately assign to. */
        return { id, name: full === "" ? "This client" : full };
      });
      setClients(named);
      setClientId((was) => (was === "" ? (named[0]?.id ?? "") : was));
    })();
    return () => { cancelled = true; };
  }, [open]);

  /* What the chosen client already has, so the plan can say `already there` BEFORE the attempt
     — the same prediction `AssignPanel` makes, from the same `planWrites`. */
  useEffect(() => {
    if (!open || clientId === "") { setExisting([]); return; }
    let cancelled = false;
    void (async () => {
      const r = await supabase.from("coach_assignments")
        .select("workout_id, root_workout_id, scheduled_date").eq("client_id", clientId);
      if (cancelled || r.error !== null) return;
      /* ⚠️ THE FIELD NAMES ARE `assign.ts`'s, NOT INVENTED HERE — my first draft called them
         `dateISO` / `rootWorkoutId` and `tsc` refused it, which is the one instrument that
         catches this class before a render does. `sourceWorkoutId` is
         `coalesce(root_workout_id, workout_id)`: the identity the unique index is keyed on. */
      setExisting((r.data ?? []).map((a) => ({
        scheduledDateISO: a.scheduled_date,
        sourceWorkoutId: a.root_workout_id ?? a.workout_id,
      })));
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  const plan = useMemo(
    () => (isMonday(startISO) ? planProgram(days, startISO, existing) : []),
    [days, startISO, existing]);
  const toWrite = plan.filter((p) => p.action === "assign").length;
  const already = plan.length - toWrite;

  async function assign() {
    if (clientId === "") { setProblem("Choose a client."); return; }
    /* The CHECK on `start_date` is `extract(isodow …) = 1`, and this says so before the round
       trip rather than letting a constraint name answer for it. */
    if (!isMonday(startISO)) { setProblem("A block starts on a Monday."); return; }
    setBusy(true); setProblem(null); setSaid(null);
    let firstId: string | null = null;
    const outcomes: WriteOutcome[] = await writeProgram(plan, async (w) => {
      const r = await supabase.rpc("assign_coach_workout", {
        p_workout_id: w.workoutId,
        p_client_id: clientId,
        p_scheduled_date: w.dateISO,
        // Non-nullable in the generated signature — an unset note is "", never null.
        p_note: w.note,
      });
      if (r.error === null) firstId ??= r.data ?? null;
      if (r.error === null && r.data !== null && deloadWeeks.includes(weekOfDate(startISO, w.dateISO))) {
        const m = await supabase.from("coach_assignments").update({ is_deload: true }).eq("id", r.data);
        if (m.error !== null) return { error: m.error };
      }
      return { error: r.error };
    });
    const tally = tallyOf(outcomes);
    tellTheClient(firstId, tally.written, programId);   // QUEUE N2c fix 2: one alert for the block, naming the program
    /* 🔴 THE RECORD IS WRITTEN ONLY IF THE LOOP FINISHED. `days_written + days_already =
       days_planned` is a CHECK on the table (§2.3) precisely so a half-run cannot record itself
       as a finished block; `tallyAddsUp` states the same equation here so the refusal is legible
       instead of arriving as a constraint name, and a partial run leaves NO record — which is
       what makes pressing Assign again a resume rather than a second block. */
    if (tallyAddsUp(tally)) {
      const r = await supabase.from("coach_program_assignments").upsert({
        program_id: programId,
        coach_id: (await supabase.auth.getUser()).data.user?.id ?? "",
        client_id: clientId,
        start_date: startISO,
        days_planned: tally.planned,
        days_written: tally.written,
        days_already: tally.already,
      }, { onConflict: "program_id,client_id,start_date" });
      /* ⚠️ A FAILED RECORD IS SAID, NOT SWALLOWED — the days ARE assigned either way, so the
         sentence has to separate what happened to the client's week from what happened to our
         note about it. Silently losing the record is how `Assigned to` starts lying. */
      if (r.error !== null) setProblem(`The days were assigned, but this block's record was not saved. ${r.error.message}`);
    }
    setBusy(false);
    setSaid(summarise(outcomes));
    onAssigned();
  }

  const chosen = clients?.find((c) => c.id === clientId) ?? null;

  return (
    <dialog ref={dialogRef} className="ht-window assign-program-window"
            aria-labelledby="assign-program-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{programName}</p>
            <h2 className="ht-title" id="assign-program-title">Assign this program</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={onClose}>Close</button>
        </header>

        <div className="ht-window-body assign-program-body">
          <div className="ht-field">
            <label className="ht-field-label" htmlFor="assign-program-client">Client</label>
            <select id="assign-program-client" ref={firstRef} className="ht-input"
                    value={clientId} onChange={(e) => { setClientId(e.target.value); setSaid(null); }}>
              {clients === null && <option value="">Loading…</option>}
              {clients !== null && clients.length === 0 && (
                <option value="">No active clients yet</option>
              )}
              {(clients ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="ht-field">
            <label className="ht-field-label" htmlFor="assign-program-start">Starts on</label>
            <input id="assign-program-start" className="ht-input" type="date" value={startISO}
                   onChange={(e) => { setStartISO(e.target.value); setSaid(null); }} />
            {/* ⚠️ THE FIELD IS A PLAIN DATE AND THE MONDAY RULE IS SAID IN WORDS, not enforced by
                a `step`. `<input type="date">` has no "Mondays only" and a `step` of 7 would
                silently forbid a legitimate correction; the CHECK is the belt, `isMonday` the
                braces, and this line is what stops a coach discovering either. */}
            {!isMonday(startISO) && (
              <p className="ht-callout assign-program-note" role="alert">
                A block starts on a Monday. The next one is {nextMondayISO(calendarToday())}.
              </p>
            )}
          </div>

          {/* 🔴 WHERE THE MISSING DAYS WENT, BEFORE THE PRESS AND NOT AFTER IT. The button
              offers the days that will actually be WRITTEN — five of a seven-day block on the
              still that prompted this line — and a count smaller than the block with nothing
              explaining it reads as the sheet having lost two days. `planWrites` already knows
              which ones and why; saying so costs a sentence and turns a surprise into a fact.
              ⚠️ It is drawn only when there ARE such days: a line saying "0 are already there"
              would be furniture on every first assignment. */}
          {already > 0 && (
            <p className="ht-body assign-program-already">
              {`${String(already)} of this block's ${String(plan.length)} days ${already === 1 ? "is" : "are"} already on their calendar and will be left alone.`}
            </p>
          )}

          {/* §3.3's sentence, so the weekly adjustment picking up after the block is not a
              surprise. It says IF IT IS SWITCHED ON because his own row is off and the default
              is FALSE. */}
          <p className="ht-body assign-program-after">{afterTheBlockSentence(weeks)}</p>
          {autoWeeksSentence(autoWeekCount) !== null && (
            <p className="ht-body assign-program-after">{autoWeeksSentence(autoWeekCount)}</p>
          )}
        </div>

        <footer className="ht-window-foot assign-program-foot">
          {problem !== null && <p className="ht-callout assign-program-problem" role="alert">{problem}</p>}
          {said !== null && <p className="ht-callout assign-program-said" role="status">{said}</p>}
          <div className="assign-program-actions">
            <button type="button" className="ht-button ht-button-primary"
                    disabled={busy || clientId === "" || !isMonday(startISO)}
                    onClick={() => { void assign(); }}>
              {busy
                ? "Assigning…"
                /* The count is the PLAN's, so it already excludes days the client has: a button
                   offering to write four days that writes one is a button nobody believes. */
                : `Assign ${String(toWrite)} day${toWrite === 1 ? "" : "s"}${chosen === null ? "" : ` to ${chosen.name}`}`}
            </button>
          </div>
        </footer>
      </div>
    </dialog>
  );
}
